import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import * as authApi from "@/api/auth";
import type { User } from "@/api/auth";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  reset: () => void;
  setTokens: (access: string, refresh: string) => void;
}

/**
 * Auth store — JWT access + refresh persisted vào localStorage qua zustand/persist.
 * Pattern: gọi `useAuthStore.getState()` từ axios interceptor (out of React tree).
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (email, password) => {
        const res = await authApi.login(email, password);
        set({
          user: res.user,
          accessToken: res.access,
          refreshToken: res.refresh,
          isAuthenticated: true,
        });
      },

      logout: async () => {
        const refresh = get().refreshToken;
        if (refresh) {
          try {
            await authApi.logout(refresh);
          } catch {
            // ignore — vẫn clear local state
          }
        }
        get().reset();
      },

      refresh: async () => {
        const refresh = get().refreshToken;
        if (!refresh) return null;
        try {
          const res = await authApi.refreshToken(refresh);
          set({ accessToken: res.access, refreshToken: res.refresh });
          return res.access;
        } catch {
          get().reset();
          return null;
        }
      },

      reset: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      setTokens: (access, refresh) => {
        set({ accessToken: access, refreshToken: refresh });
      },
    }),
    {
      name: "sep2026-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
